import { mapState } from 'vuex';
import BridgeMonitoringCharts from './BridgeMonitoringCharts.vue';
import BridgeMonitoringRules from './BridgeMonitoringRules.vue';
import ApiService from '@/services/apiService';
import mixinMonitoringPanel from './mixinMonitoringPanel';
export default {
  name: 'CoreConnectorsBridgeMonitoring',
  components: {
    BridgeMonitoringCharts,
    BridgeMonitoringRules
  },
  mixins: [mixinMonitoringPanel],
  props: {
    bridgeId: {
      type: Number,
      required: true
    },
    tenantId: {
      type: Number,
      required: true
    },
    monitoringConfig: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      evaluationIntervalMinutes: 5,
      // Allowed values (in minutes) must match ALLOWED_EVALUATION_INTERVALS_SEC
      // in backend/unify-service/services/bridge-manager/monitoring/constants.js
      evaluationIntervalOptions: [1, 3, 5, 10, 15],
      selectedTimeFrame: null,
      timeFrames: [{
        hours: 6,
        name: `${this.$i18n_t('Common.last')} 6 ${this.$i18n_t('Common.hours')}`
      }, {
        hours: 12,
        name: `${this.$i18n_t('Common.last')} 12 ${this.$i18n_t('Common.hours')}`
      }, {
        days: 1,
        name: this.$i18n_t('Common.last24Hours')
      }, {
        days: 2,
        name: `${this.$i18n_t('Common.last')} 2 ${this.$i18n_t('Common.days')}`
      }, {
        days: 3,
        name: `${this.$i18n_t('Common.last')} 3 ${this.$i18n_t('Common.days')}`
      }, {
        days: 4,
        name: `${this.$i18n_t('Common.last')} 4 ${this.$i18n_t('Common.days')}`
      }, {
        days: 5,
        name: `${this.$i18n_t('Common.last')} 5 ${this.$i18n_t('Common.days')}`
      }, {
        days: 6,
        name: `${this.$i18n_t('Common.last')} 6 ${this.$i18n_t('Common.days')}`
      }, {
        days: 7,
        name: this.$i18n_t('Common.lastWeek')
      }],
      timeRange: {
        from: null,
        // Unix timestamp
        to: null // Unix timestamp
      },
      emailConnectors: [],
      webhookConnectors: [],
      webhookFormatOptions: [{
        text: 'Default JSON',
        value: 'default'
      }, {
        text: 'Slack',
        value: 'slack'
      }, {
        text: 'Microsoft Teams',
        value: 'teams'
      }],
      // local state for email settings
      emailSettings: {
        connectorId: null,
        senderEmail: null,
        recipientEmail: null
      },
      savingEmail: false,
      // local state for webhook settings
      webhookSettings: {
        connectorId: null,
        format: 'default'
      },
      savingWebhook: false
    };
  },
  computed: {
    ...mapState('app', ['color']),
    isPaused() {
      var _this$monitoringConfi;
      return !((_this$monitoringConfi = this.monitoringConfig) !== null && _this$monitoringConfi !== void 0 && _this$monitoringConfi.enabled);
    }
  },
  async mounted() {
    this.parseEvaluationInterval();
    // set default time frame to 1 day (last 24 hours)
    this.selectedTimeFrame = this.timeFrames[2]; // index 2 is "Last 24 Hours"
    this.calculateTimeRange(this.selectedTimeFrame);

    // fetch available connectors for notifications
    await this.fetchNotificationConnectors();

    // initialize local settings from monitoring config
    this.initializeSettings();
  },
  methods: {
    handleTimeFrameChange(timeFrame) {
      this.calculateTimeRange(timeFrame);
    },
    calculateTimeRange(timeFrame) {
      // calculate Unix timestamps for the selected time range
      const now = Math.floor(Date.now() / 1000); // current time in seconds
      let secondsToSubtract;
      if (timeFrame.hours) {
        secondsToSubtract = timeFrame.hours * 3600; // hours * 60 * 60
      } else if (timeFrame.days) {
        secondsToSubtract = timeFrame.days * 86400; // days * 24 * 60 * 60
      }
      const from = now - secondsToSubtract;
      this.timeRange = {
        from,
        to: now
      };
    },
    refreshCharts() {
      // recalculate time range from current moment to get fresh data
      if (this.selectedTimeFrame) {
        this.calculateTimeRange(this.selectedTimeFrame);
      }
    },
    formatTimeRange(fromTimestamp, toTimestamp) {
      const formatDate = timestamp => {
        const date = new Date(timestamp * 1000);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      };
      return `${formatDate(fromTimestamp)} - ${formatDate(toTimestamp)}`;
    },
    parseEvaluationInterval() {
      var _this$monitoringConfi2;
      const props = (_this$monitoringConfi2 = this.monitoringConfig) === null || _this$monitoringConfi2 === void 0 ? void 0 : _this$monitoringConfi2.properties;
      const intervalInSeconds = (props === null || props === void 0 ? void 0 : props.evaluationInterval) || 300;
      const minutes = Math.max(1, Math.round(intervalInSeconds / 60));
      // Clamp to nearest valid option to prevent blank dropdown
      const closest = this.evaluationIntervalOptions.reduce((prev, curr) => Math.abs(curr - minutes) < Math.abs(prev - minutes) ? curr : prev);
      this.evaluationIntervalMinutes = closest;
    },
    async fetchNotificationConnectors() {
      try {
        const bridges = await ApiService.getBridgesForTenant(this.tenantId);
        this.emailConnectors = bridges.filter(bridge => bridge.type === 'bridge-mail').map(bridge => {
          const isActive = bridge.state === 'active';
          return {
            id: bridge.id,
            name: isActive ? bridge.name : `${bridge.name} (inactive)`,
            disabled: !isActive
          };
        });
        this.webhookConnectors = bridges.filter(bridge => bridge.type === 'bridge-webhook').map(bridge => {
          const isActive = bridge.state === 'active';
          return {
            id: bridge.id,
            name: isActive ? bridge.name : `${bridge.name} (inactive)`,
            disabled: !isActive
          };
        });
      } catch (error) {
        console.error('Failed to fetch notification connectors:', error);
        this.$snack('Failed to load notification connectors', 'error');
      }
    },
    async updateEvaluationInterval() {
      try {
        const intervalInSeconds = this.evaluationIntervalMinutes * 60;
        const updatedConfig = {
          ...this.monitoringConfig,
          properties: {
            ...this.monitoringConfig.properties,
            evaluationInterval: intervalInSeconds
          }
        };
        await ApiService.updateMonitoringConfig(this.monitoringConfig.id, updatedConfig);
        this.$snack(this.$i18n_t('Common.snacks.saveSuccess'), 'success');
        this.$emit('config-changed');
      } catch (error) {
        this.$snack(error.message, 'error');
      }
    },
    initializeSettings() {
      var _this$monitoringConfi3, _this$monitoringConfi4, _this$monitoringConfi5, _this$monitoringConfi6, _this$monitoringConfi7;
      this.emailSettings = {
        connectorId: ((_this$monitoringConfi3 = this.monitoringConfig.properties) === null || _this$monitoringConfi3 === void 0 ? void 0 : _this$monitoringConfi3.emailConnectorId) || null,
        senderEmail: ((_this$monitoringConfi4 = this.monitoringConfig.properties) === null || _this$monitoringConfi4 === void 0 ? void 0 : _this$monitoringConfi4.senderEmail) || null,
        recipientEmail: ((_this$monitoringConfi5 = this.monitoringConfig.properties) === null || _this$monitoringConfi5 === void 0 ? void 0 : _this$monitoringConfi5.notificationEmail) || null
      };
      this.webhookSettings = {
        connectorId: ((_this$monitoringConfi6 = this.monitoringConfig.properties) === null || _this$monitoringConfi6 === void 0 ? void 0 : _this$monitoringConfi6.webhookConnectorId) || null,
        format: ((_this$monitoringConfi7 = this.monitoringConfig.properties) === null || _this$monitoringConfi7 === void 0 ? void 0 : _this$monitoringConfi7.webhookFormat) || 'default'
      };
    },
    async saveEmailSettings() {
      this.savingEmail = true;
      try {
        const updatedConfig = {
          ...this.monitoringConfig,
          properties: {
            ...this.monitoringConfig.properties,
            emailConnectorId: this.emailSettings.connectorId,
            senderEmail: this.emailSettings.senderEmail,
            notificationEmail: this.emailSettings.recipientEmail
          }
        };
        await ApiService.updateMonitoringConfig(this.monitoringConfig.id, updatedConfig);
        this.$snack(this.$i18n_t('Common.snacks.saveSuccess'), 'success');
        this.$emit('config-changed');
      } catch (error) {
        this.$snack(error.message, 'error');
      } finally {
        this.savingEmail = false;
      }
    },
    async saveWebhookSettings() {
      this.savingWebhook = true;
      try {
        const updatedConfig = {
          ...this.monitoringConfig,
          properties: {
            ...this.monitoringConfig.properties,
            webhookConnectorId: this.webhookSettings.connectorId,
            webhookFormat: this.webhookSettings.format
          }
        };
        await ApiService.updateMonitoringConfig(this.monitoringConfig.id, updatedConfig);
        this.$snack(this.$i18n_t('Common.snacks.saveSuccess'), 'success');
        this.$emit('config-changed');
      } catch (error) {
        this.$snack(error.message, 'error');
      } finally {
        this.savingWebhook = false;
      }
    }
  }
};